import { orderBy, where } from "firebase/firestore";
import { COL, SUB, SETTINGS_DOC_ID } from "./collections.js";
import { useDoc } from "./useDoc.js";
import { useCollection } from "./useCollection.js";
import { toDate } from "../utils/format.js";

/* ---- Site settings (single doc) ---- */
export function useSiteSettings() {
  const { data, loading } = useDoc(COL.siteSettings, SETTINGS_DOC_ID);
  return { settings: data, loading };
}

/* ---- Pages + sections ---- */
export function usePages() {
  const { data, loading } = useCollection(COL.pages, [orderBy("name")]);
  return { pages: data, loading };
}

export function usePage(slug) {
  const { data: page, loading: pageLoading } = useDoc(COL.pages, slug);
  const { data: sections, loading: secLoading } = useCollection(
    slug ? [COL.pages, slug, SUB.sections] : COL.pages,
    [orderBy("order")]
  );
  return { page, sections, loading: pageLoading || secLoading };
}

/* ---- Programs ---- */
/* Public page: filter `published == true` IN the query (the security rules
   require it for non-admins) but DON'T orderBy in the query — that combination
   would need a composite index. Sort by `order` client-side instead.
   Admin (publishedOnly false) is authenticated, so a plain orderBy is fine. */
export function usePrograms({ publishedOnly = false } = {}) {
  const constraints = publishedOnly ? [where("published", "==", true)] : [orderBy("order")];
  const { data, loading } = useCollection(COL.programs, constraints);
  const programs = publishedOnly
    ? [...data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : data;
  return { programs, loading };
}

export function useProgram(slug) {
  const { data, loading } = useDoc(COL.programs, slug);
  return { program: data, loading };
}

/* ---- Events ---- */
/* Same pattern as programs: the public page filters `published == true` in the
   query (the security rules require it for non-admins) but DON'T orderBy in the
   query — that combination needs a composite index. Sort by startAt (newest
   first) client-side instead. Admin is authenticated, so a plain orderBy works. */
export function useEvents({ publishedOnly = false } = {}) {
  const constraints = publishedOnly ? [where("published", "==", true)] : [orderBy("startAt", "desc")];
  const { data, loading } = useCollection(COL.events, constraints);
  const events = publishedOnly
    ? [...data].sort((a, b) => (toDate(b.startAt)?.getTime() || 0) - (toDate(a.startAt)?.getTime() || 0))
    : data;
  return { events, loading };
}

export function useEvent(slug) {
  const { data, loading } = useDoc(COL.events, slug);
  return { event: data, loading };
}

/* ---- Gallery ---- */
/* Fetch unordered (no composite index needed) and sort client-side by edition
   (newest tag first) then `order`. Keeps the gallery resilient even if the
   editionTag+order composite index isn't deployed. */
export function useGallery() {
  const { data, loading } = useCollection(COL.gallery);
  const gallery = [...data].sort((a, b) => {
    const tagA = a.editionTag || "";
    const tagB = b.editionTag || "";
    if (tagA !== tagB) return tagB.localeCompare(tagA); // desc
    return (a.order ?? 0) - (b.order ?? 0); // asc
  });
  return { gallery, loading };
}

/* ---- Blog posts ---- */
export function usePosts({ publishedOnly = false } = {}) {
  const constraints = publishedOnly
    ? [where("status", "==", "published"), orderBy("publishedAt", "desc")]
    : [orderBy("updatedAt", "desc")];
  const { data, loading } = useCollection(COL.blogPosts, constraints);
  return { posts: data, loading };
}

export function usePost(id) {
  const { data, loading } = useDoc(COL.blogPosts, id);
  return { post: data, loading };
}

/* ---- Inbound: leads / enquiries / donations (admin) ---- */
export function useLeads() {
  const { data, loading } = useCollection(COL.donorLeads, [orderBy("submittedAt", "desc")]);
  return { leads: data, loading };
}
export function useEnquiries() {
  const { data, loading } = useCollection(COL.enquiries, [orderBy("submittedAt", "desc")]);
  return { enquiries: data, loading };
}
export function useDonations() {
  const { data, loading } = useCollection(COL.donations, [orderBy("createdAt", "desc")]);
  return { donations: data, loading };
}
export function useSchoolGroups() {
  const { data, loading } = useCollection(COL.schoolGroups, [orderBy("submittedAt", "desc")]);
  return { groups: data, loading };
}
